import React, { useState } from 'react';
import { X, CreditCard, Building2, DollarSign, Coins } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'stripe', label: 'Stripe (Card)', icon: CreditCard, enabled: true },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2, enabled: true },
  { id: 'paypal', label: 'PayPal', icon: DollarSign, enabled: true, mocked: true },
  { id: 'crypto', label: 'Cryptocurrency', icon: Coins, enabled: true, mocked: true }
];

const AddPaymentModal = ({ isOpen, onClose, invoice, onSuccess }) => {
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [formData, setFormData] = useState({
    amount: invoice.amount_outstanding,
    currency: invoice.currency,
    payment_date: new Date().toISOString().split('T')[0],
    bank_reference: '',
    transaction_id: '',
    crypto_wallet: '',
    crypto_tx_hash: '',
    notes: ''
  });

  const handleStripePayment = async () => {
    try {
      setLoading(true);

      const origin = window.location.origin;

      const response = await fetch(
        `${API_URL}/api/procurement/invoices/payments/stripe/intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            invoice_id: invoice.id,
            amount: formData.amount,
            currency: formData.currency,
            return_url: origin
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create Stripe checkout');
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating Stripe payment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate Stripe payment',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const handleBankTransferPayment = async () => {
    try {
      setLoading(true);

      const payload = {
        invoice_id: invoice.id,
        payment_method: 'bank_transfer',
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        payment_date: new Date(formData.payment_date).toISOString(),
        bank_reference: formData.bank_reference,
        notes: formData.notes
      };

      const response = await fetch(
        `${API_URL}/api/procurement/invoices/payments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to record bank transfer');
      }

      const data = await response.json();

      toast({
        title: 'Success',
        description: `Payment ${data.payment_number} recorded (status: PENDING - awaiting confirmation)`
      });

      onSuccess();
    } catch (error) {
      console.error('Error recording bank transfer:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to record bank transfer',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMockedPayment = (method) => {
    toast({
      title: 'Feature Coming Soon',
      description: `${method} payment integration is currently in development. Please use Stripe or Bank Transfer.`,
      variant: 'default'
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedMethod === 'stripe') {
      handleStripePayment();
    } else if (selectedMethod === 'bank_transfer') {
      handleBankTransferPayment();
    } else if (selectedMethod === 'paypal') {
      handleMockedPayment('PayPal');
    } else if (selectedMethod === 'crypto') {
      handleMockedPayment('Cryptocurrency');
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-[#2B2D42]">Add Payment</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Invoice Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#6C757D]">Invoice:</span>
                  <span className="font-medium text-[#2B2D42]">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6C757D]">Total Amount:</span>
                  <span className="font-medium text-[#2B2D42]">
                    {formatCurrency(invoice.total_amount, invoice.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6C757D]">Amount Paid:</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(invoice.amount_paid, invoice.currency)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-bold text-[#2B2D42]">Outstanding:</span>
                  <span className="font-bold text-[#FF6B35]">
                    {formatCurrency(invoice.amount_outstanding, invoice.currency)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-3">
                Select Payment Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setSelectedMethod(method.id)}
                      className={`relative flex items-center space-x-3 p-4 border-2 rounded-lg transition-all ${
                        selectedMethod === method.id
                          ? 'border-[#FF6B35] bg-[#FF6B35] bg-opacity-5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${selectedMethod === method.id ? 'text-[#FF6B35]' : 'text-[#6C757D]'}`} />
                      <div className="text-left">
                        <div className={`font-medium ${selectedMethod === method.id ? 'text-[#2B2D42]' : 'text-[#6C757D]'}`}>
                          {method.label}
                        </div>
                        {method.mocked && (
                          <div className="text-xs text-yellow-600 mt-1">UI Only (Coming Soon)</div>
                        )}
                      </div>
                      {selectedMethod === method.id && (
                        <div className="absolute top-2 right-2 h-2 w-2 bg-[#FF6B35] rounded-full"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Payment Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                  Amount *
                </label>
                <input
                  type="number"
                  required
                  min="0.01"
                  max={invoice.amount_outstanding}
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                />
                <div className="text-xs text-[#6C757D] mt-1">
                  Max: {formatCurrency(invoice.amount_outstanding, invoice.currency)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                  Payment Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                />
              </div>
            </div>

            {/* Method-specific fields */}
            {selectedMethod === 'bank_transfer' && (
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                  Bank Reference Number
                </label>
                <input
                  type="text"
                  placeholder="e.g., TRX123456789"
                  value={formData.bank_reference}
                  onChange={(e) => setFormData({ ...formData, bank_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                />
              </div>
            )}

            {selectedMethod === 'crypto' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Wallet Address
                  </label>
                  <input
                    type="text"
                    placeholder="0x..."
                    value={formData.crypto_wallet}
                    onChange={(e) => setFormData({ ...formData, crypto_wallet: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Transaction Hash
                  </label>
                  <input
                    type="text"
                    placeholder="0x..."
                    value={formData.crypto_tx_hash}
                    onChange={(e) => setFormData({ ...formData, crypto_tx_hash: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                placeholder="Add any additional notes about this payment..."
              />
            </div>

            {/* Info Box */}
            {selectedMethod === 'stripe' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <p className="font-medium mb-1">Stripe Checkout</p>
                <p>You will be redirected to Stripe's secure checkout page to complete the payment.</p>
              </div>
            )}

            {selectedMethod === 'bank_transfer' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                <p className="font-medium mb-1">Manual Confirmation Required</p>
                <p>Payment will be recorded as PENDING until manually confirmed by admin.</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B35] hover:bg-[#E5562D]"
              >
                {loading ? 'Processing...' : 
                 selectedMethod === 'stripe' ? 'Proceed to Stripe' :
                 selectedMethod === 'bank_transfer' ? 'Record Payment' :
                 'Continue'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;
